// Analytics tracking utility
class AnalyticsTracker {
  constructor() {
    this.currentSessionId = null;
    this.sessionStartTime = null;
    this.pagesVisited = [];
    this.currentProductViewId = null;
    this.productViewStartTime = null;
    this.isTracking = false;
  }

  // Initialize tracking
  init() {
    if (this.isTracking) return;
    
    console.log('🔍 Analytics: Initializing tracking...');
    this.isTracking = true;
    this.startSession();
    this.setupEventListeners();
    console.log('🔍 Analytics: Tracking initialized');
  }

  // Start a new user session
  async startSession() {
    console.log('🔍 Analytics: Starting session...');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const deviceInfo = this.getDeviceInfo();
      
      console.log('🔍 Analytics: User ID:', user.id);
      
      const response = await fetch(`${process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:5000/api/analytics'}/session/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id || null,
          device_info: JSON.stringify(deviceInfo),
          ip_address: null // Will be captured on server side
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.currentSessionId = data.session_id;
        this.sessionStartTime = new Date();
        console.log('🔍 Analytics: Session started successfully:', this.currentSessionId);
      } else {
        console.error('🔍 Analytics: Failed to start session, status:', response.status);
        const errorText = await response.text();
        console.error('🔍 Analytics: Error response:', errorText);
      }
    } catch (error) {
      console.error('🔍 Analytics: Failed to start analytics session:', error);
    }
  }

  // End the current session
  async endSession() {
    if (!this.currentSessionId) return;

    try {
      await fetch(`${process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:5000/api/analytics'}/session/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: this.currentSessionId,
          pages_visited: this.pagesVisited
        })
      });

      console.log('Analytics session ended:', this.currentSessionId);
      this.currentSessionId = null;
      this.sessionStartTime = null;
      this.pagesVisited = [];
    } catch (error) {
      console.error('Failed to end analytics session:', error);
    }
  }

  // Track page visit
  trackPageVisit(pageName) {
    if (!this.isTracking) return;

    this.pagesVisited.push({
      page: pageName,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    console.log('Page visited:', pageName);
  }

  // Track product view
  async trackProductView(productId, interactionType = 'view') {
    if (!this.isTracking) {
      console.log('🔍 Analytics: Tracking not initialized, skipping product view');
      return;
    }

    console.log('🔍 Analytics: Tracking product view:', productId, interactionType);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const deviceInfo = this.getDeviceInfo();

      const response = await fetch(`${process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:5000/api/analytics'}/product/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          user_id: user.id || null,
          session_id: this.currentSessionId,
          device_info: JSON.stringify(deviceInfo),
          interaction_type: interactionType
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.currentProductViewId = data.view_id;
        this.productViewStartTime = new Date();
        console.log('🔍 Analytics: Product view tracked successfully:', productId, interactionType);
      } else {
        console.error('🔍 Analytics: Failed to track product view, status:', response.status);
        const errorText = await response.text();
        console.error('🔍 Analytics: Error response:', errorText);
      }
    } catch (error) {
      console.error('🔍 Analytics: Failed to track product view:', error);
    }
  }

  // Update product view time spent
  async updateProductViewTime(timeSpentSeconds) {
    if (!this.currentProductViewId) return;

    try {
      await fetch(`${process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:5000/api/analytics'}/product/view/time`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          view_id: this.currentProductViewId,
          time_spent_seconds: timeSpentSeconds
        })
      });

      this.currentProductViewId = null;
      this.productViewStartTime = null;
    } catch (error) {
      console.error('Failed to update product view time:', error);
    }
  }

  // Track product interaction (add to cart, purchase, etc.)
  async trackProductInteraction(productId, interactionType) {
    await this.trackProductView(productId, interactionType);
  }

  // Get device and browser information
  getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  }

  // Setup event listeners for automatic tracking
  setupEventListeners() {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden, end product view if active
        if (this.currentProductViewId && this.productViewStartTime) {
          const timeSpent = Math.floor((new Date() - this.productViewStartTime) / 1000);
          this.updateProductViewTime(timeSpent);
        }
      }
    });

    // Track beforeunload (page close/refresh)
    window.addEventListener('beforeunload', () => {
      // End product view if active
      if (this.currentProductViewId && this.productViewStartTime) {
        const timeSpent = Math.floor((new Date() - this.productViewStartTime) / 1000);
        this.updateProductViewTime(timeSpent);
      }
      
      // End session
      this.endSession();
    });

    // Track route changes (for SPA)
    let currentPath = window.location.pathname;
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPath) {
        currentPath = window.location.pathname;
        this.trackPageVisit(currentPath);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Manual page tracking (for React Router)
  trackRouteChange(pathname) {
    this.trackPageVisit(pathname);
  }

  // Get current session info
  getSessionInfo() {
    return {
      sessionId: this.currentSessionId,
      startTime: this.sessionStartTime,
      pagesVisited: this.pagesVisited.length
    };
  }
}

// Create singleton instance
const analyticsTracker = new AnalyticsTracker();

// Export the tracker instance
export default analyticsTracker;

// Export convenience functions
export const trackPageVisit = (pageName) => analyticsTracker.trackPageVisit(pageName);
export const trackProductView = (productId, interactionType) => analyticsTracker.trackProductView(productId, interactionType);
export const trackProductInteraction = (productId, interactionType) => analyticsTracker.trackProductInteraction(productId, interactionType);
export const initAnalytics = () => analyticsTracker.init();
export const getSessionInfo = () => analyticsTracker.getSessionInfo();
